import { useCallback } from 'react';
import { loginAuthDefer } from '../useLoginAuth';
import {
  uploadImgFile_v2,
  getUploadUrls,
} from '@/service/uploadImgService/newUploadService';
import type { BaseImageStatus } from '@/components/batchToolsComp/layoutContext/ImageProcessingContext';
import { timestampToFormattedTime } from '@/utils/timestampToTime';
import { feBot } from '@/monitor';

/**
 * 纯粹的图像上传Hook
 * @description 提供通用的图像上传功能，不包含业务逻辑
 */
export function useCommonImageUpload() {
  /**
   * 批量上传图像文件
   * @param images 待上传的图像状态数组
   * @param onProgress 上传进度回调
   * @param onComplete 单个图像上传完成回调
   */
  const uploadImages = useCallback(
    async <T extends BaseImageStatus>(
      images: T[],
      from: string = 'uploadImages_default_from',
      onProgress?: (
        imageId: string,
        status: 'uploading' | 'success' | 'failed',
        message?: string
      ) => void,
      onComplete?: (
        imageId: string,
        result: {
          success: boolean;
          url?: string;
          fileId?: string;
          error?: string;
        }
      ) => void
    ) => {
      try {
        await loginAuthDefer.promise;

        if (images.length === 0) return;

        // console.log(
        //   `检测、初始化回显、缓存完${images.length}张图像，即将准备上传:`,
        //   timestampToFormattedTime()
        // );

        // 获取上传链接
        const files = images.map((img) => img.localFile);
        const uploadResults = await getUploadUrls(files);

        // 并行上传所有图像
        const uploadPromises = images.map(async (imageStatus, index) => {
          const imageId = imageStatus.localImgId;
          const uploadInfo = uploadResults[index];

          // 通知开始上传
          onProgress?.(imageId, 'uploading', 'uploading...');

          if (!uploadInfo || !uploadInfo.upload_url || !uploadInfo.file_id) {
            const error = 'Missing upload URL or file ID';
            console.error(
              `图像 ${imageStatus.localImgName} 缺少上传信息:`,
              uploadInfo
            );

            onProgress?.(imageId, 'failed', error);
            onComplete?.(imageId, { success: false, error });
            return;
          }

          try {
            const result = await uploadImgFile_v2(
              uploadInfo.upload_url,
              imageStatus.localFile,
              from
            );

            if (result && result.success) {
              onProgress?.(imageId, 'success', 'upload success');
              onComplete?.(imageId, {
                success: true,
                url: result.url,
                fileId: uploadInfo.file_id,
              });
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            console.error(`上传图像失败: ${errorMessage}`);

            onProgress?.(imageId, 'failed', `upload failed: ${errorMessage}`);
            onComplete?.(imageId, { success: false, error: errorMessage });

            feBot.lark_report({
              title: '[UPLOAD_ERROR] uploadImgFile_v2 callback error',
              type: from,
              errorInfo: JSON.stringify(error),
              isRPostHog: true,
            });
          }
        });

        await Promise.allSettled(uploadPromises);

        // console.log(
        //   `上传完成${images.length}张图像:`,
        //   timestampToFormattedTime()
        // );
      } catch (error) {
        console.error('批量上传图像出错:', error);
        feBot.lark_report({
          title: '[UPLOAD_ERROR] uploadImages ',
          type: from,
          errorInfo: JSON.stringify(error),
          isRPostHog: true,
        });
      }
    },
    []
  );

  // /**
  //  * 上传单个图像文件
  //  * @param file 文件对象
  //  * @param imageId 图像ID
  //  */
  // const uploadSingleImage = useCallback(async (file: File, imageId: string) => {
  //   try {
  //     await loginAuthDefer.promise;

  //     const uploadResults = await getUploadUrls([file]);
  //     const uploadInfo = uploadResults[0];

  //     if (!uploadInfo || !uploadInfo.upload_url || !uploadInfo.file_id) {
  //       throw new Error('Missing upload URL or file ID');
  //     }

  //     const result = await uploadImgFile_v2(uploadInfo.upload_url, file);

  //     if (result && result.success) {
  //       return {
  //         success: true,
  //         url: result.url,
  //         fileId: uploadInfo.file_id,
  //       };
  //     }

  //     throw new Error('Upload failed');
  //   } catch (error) {
  //     const errorMessage =
  //       error instanceof Error ? error.message : String(error);
  //     return {
  //       success: false,
  //       error: errorMessage,
  //     };
  //   }
  // }, []);

  return {
    uploadImages,
    // uploadSingleImage,
  };
}

export default useCommonImageUpload;
